import { VisitPriorityIconWithTooltip } from '../icons/PriorityIcon';
import { NoneLabel } from '../labels/NoneLabel';
import { CenteredSortHeadCell, CustomHeadRenderParams } from '../tables/customCells';
import { VisitRequestActionsCellWithMenu } from '../visitRequests/tables/VisitRequestActionsCell';
import { VisitActionsCell } from '../visits/tables/VisitActionsCell';
import { VisitColumns } from '../visits/tables/columns';
import {
    UnifiedVisitOccurrenceRow,
    isUnifiedVisit,
    isUnifiedVisitRequest,
} from './UnifiedVisitOccurrencesTable';

export namespace OrderVisitColumns {
    // The Actions column only holds a three-dot menu, so keep it narrow rather
    // than letting mui-datatables pad it out into needless horizontal overflow.
    const actionsCellProps = { sx: { width: '44px', paddingLeft: '4px', paddingRight: '4px' } };

    export const actionsColumn = {
        // Label blank: the three-dot menu is self-explanatory and header text just
        // costs horizontal space.
        label: '',
        // Right-pinning is opt-in per caller via the table's `pinActions` prop, so
        // the menu stays reachable on a wide, horizontally scrolled table.
        rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
            if (isUnifiedVisit(row)) {
                return <VisitActionsCell visitId={row.id} />;
            }
            if (isUnifiedVisitRequest(row)) {
                return <VisitRequestActionsCellWithMenu visitRequest={row} />;
            }
            return <NoneLabel />;
        },
        options: {
            setCellProps: () => actionsCellProps,
            setCellHeaderProps: () => actionsCellProps,
        },
    };

    export const priorityColumn = {
        field: 'sortPriority',
        // Label blank: the priority icon carries the meaning. A centered,
        // always-visible sort control replaces the header text so the column
        // reads as sortable, and customHeadRender stops mui falling back to
        // rendering the `sortPriority` field name.
        label: '',
        rowToComponent: (row: UnifiedVisitOccurrenceRow) => {
            return <VisitPriorityIconWithTooltip priority={row.displayPriority} />;
        },
        options: {
            ...VisitColumns.priorityColumnOptions,
            customHeadRender: (
                columnMeta: CustomHeadRenderParams[0],
                handleToggleColumn: CustomHeadRenderParams[1],
                sortOrder: CustomHeadRenderParams[2],
            ) => (
                <CenteredSortHeadCell
                    key={columnMeta.index}
                    columnMeta={columnMeta}
                    handleToggleColumn={handleToggleColumn}
                    sortOrder={sortOrder}
                    label='Priority'
                />
            ),
        },
    };

    /** Sort key for Order column: order's fulfillment window start date as timestamp (0 if none). */
    export const createOrderSortKey = (
        order: { fulfillmentWindow: { startDate: Date | string | null } | null } | null,
    ) => {
        if (!order?.fulfillmentWindow?.startDate) {
            return 0;
        }
        return new Date(order.fulfillmentWindow.startDate).getTime();
    };
}
